import { Button } from "@package/subframe/components/Button";
import { IconWithLogoBackground } from "@package/subframe/components/IconWithLogoBackground";
import { FeatherTrash2 } from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useDeleteTaskDefinitionCommandControllerDelete } from "@/generated/compliance-tasks/compliance-tasks";
import type { ComplianceTaskDefinitionResponseDto } from "@/generated/model/complianceTaskDefinitionResponseDto";
import {
  extractApiErrorMessage,
  invalidateComplianceQueries,
} from "@/lib/compliance/invalidate-compliance-queries";

interface DeleteDefinitionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  definition: ComplianceTaskDefinitionResponseDto;
}

export function DeleteDefinitionDialog({
  open,
  onOpenChange,
  definition,
}: DeleteDefinitionDialogProps) {
  const queryClient = useQueryClient();
  const deleteDefinition = useDeleteTaskDefinitionCommandControllerDelete();
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await deleteDefinition.mutateAsync({ definitionId: definition.id });
      toast.success(`"${definition.name}" has been deleted`);
      await invalidateComplianceQueries(queryClient);
      onOpenChange(false);
    } catch (error: unknown) {
      toast.error(
        extractApiErrorMessage(error, `Failed to delete "${definition.name}"`),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false} className="max-w-[480px] p-0">
        <DialogTitle className="sr-only">Delete compliance task</DialogTitle>
        <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-white">
          <div className="flex max-w-[448px] flex-col items-center gap-6 p-4">
            <div className="flex flex-col items-center gap-6">
              <IconWithLogoBackground
                variant="error"
                size="x-large"
                icon={<FeatherTrash2 />}
              />
              <div className="flex flex-col items-center gap-2">
                <span className="w-full text-center text-heading-2 font-heading-2 text-default-font">
                  Delete task?
                </span>
                <span className="text-center text-body font-body text-subtext-color">
                  Are you sure you want to permanently delete{" "}
                  <strong>{definition.name}</strong>? Its pending instances and
                  uploaded evidence will be removed. This cannot be undone — if
                  the task has simply run its course, deactivate it instead.
                </span>
              </div>
            </div>
          </div>
          <div className="flex w-full flex-col items-center gap-3 bg-white px-4 py-4">
            <Button
              className="h-14 w-80 flex-none"
              variant="neutral-secondary"
              size="extra-large"
              onClick={() => onOpenChange(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              className="h-14 w-80 flex-none"
              variant="destructive-primary"
              size="extra-large"
              onClick={handleConfirm}
              disabled={busy}
            >
              {busy ? "Deleting..." : "Yes, delete"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
